import { useCallback, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import WysiwygEditor from 'components/atoms/WysiwygEditor';
import Input from 'components/molecules/Input';
import Typography from 'components/atoms/Typography';
import { DiscoverItemType, HexColor, KitResource, KitResourceId, Kits } from 'utils/gloo-types';
import CloudinaryImageUploader from 'components/organisms/CloudinaryImageUpload';
import KIT_BANNER from 'assets/Kit_banner.png';
import KIT_THUMBNAIL from 'assets/Kit_card.png';
import SelectKeywords from 'components/molecules/SelectKeywords';
import Card from 'components/atoms/Card';
import { CompactPicker } from 'react-color';
import Separator from 'components/atoms/Separator';
import KitAuthoringResourcesList from 'pages/_AdminAuthoringPages/_components/KitAuthoringResourcesList';
import { ToggleControlled } from 'components/molecules/Toggle';
import { useThunkDispatch } from 'utils/reduxHook';
import { getKit, getTemplate } from 'actions/requests';
import { kitResourceForKit, kitResourceForTemplate } from 'utils/discover';
import Select from 'components/molecules/Select';
import { orgTypeFilteringOptions } from 'utils/selectOptions';
import { SelectOption } from 'utils/frontendTypes';
import KitBannerPreview from './KitBannerPreview';

interface KitFormProps {
  formId: string;
  kit?: Kits;
  handleOnChange: (data) => void;
  handleKitSave: (data: Partial<Kits>) => void;
}

function KitForm({ formId, kit, handleOnChange, handleKitSave }: KitFormProps) {
  const dispatch = useThunkDispatch();

  // For backward compatibility, if tags were set previously but topics are empty,
  // populate the topics with the tags so that the topics will exist in the row upon save
  if (!kit?.metadata?.topics && kit?.tags) {
    kit.metadata = {
      ...kit?.metadata,
      topics: [...kit?.tags],
    };
  }

  const [updatedKit, setUpdatedKit] = useState<Partial<Kits>>(
    kit || {
      title: '',
      subtitle: '',
      shortDescription: '',
      longDescription: '',
      instructions: '',
      tags: [],
      bannerImageUrl: '',
      thumbnailImageUrl: '',
      metadata: {
        author: '',
        topics: [],
      },
      branding: {},
      templates: [],
      templateIds: [],
      kitResourceIds: [],
      kitResources: [],
      visibleInDiscover: false,
      orgTypeFiltering: 'church',
    },
  );
  const [kitResources, setKitResources] = useState<KitResource[]>(updatedKit.kitResources);
  const loadKitResources = useCallback(
    async (kitResourceIds: KitResourceId[]) => {
      const newKitResources = {};
      // eslint-disable-next-line no-restricted-syntax
      for (const kitResourceId of kitResourceIds) {
        // load the Kit or Template
        if (kitResourceId.type === DiscoverItemType.KIT) {
          // eslint-disable-next-line no-await-in-loop
          const resourceKit = await dispatch(getKit(kitResourceId.id));
          newKitResources[kitResourceId.id] = kitResourceForKit(resourceKit);
        }
        if (kitResourceId.type === DiscoverItemType.TEMPLATE) {
          // eslint-disable-next-line no-await-in-loop
          const resourceTemplate = await dispatch(getTemplate(kitResourceId.id));
          newKitResources[kitResourceId.id] = kitResourceForTemplate(resourceTemplate);
        }
      }
      const sortedKitResources = kitResourceIds.map((kitResourceId) => {
        return newKitResources[kitResourceId.id];
      });
      setKitResources(sortedKitResources);
    },
    [setKitResources],
  );

  useEffect(() => {
    loadKitResources(updatedKit.kitResourceIds);
  }, []);

  useEffect(() => {
    setUpdatedKit({
      ...updatedKit,
      kitResources,
    });
  }, [kitResources]);

  useEffect(() => {
    if (kit?.archivedAt) {
      setUpdatedKit({
        ...updatedKit,
        featuredPriority: null,
        visibleInDiscover: false,
      });
    }
  }, [kit?.archivedAt]);

  const formSchema = yup.object().shape({
    title: yup.string().required('A title is required.'),
    subtitle: yup.string(),
    shortDescription: yup.string().required('A short description is required.'),
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(formSchema),
  });

  useEffect(() => {
    // Hydrate useForm with initial values
    if (kit) {
      setValue('title', kit.title);
      setValue('subtitle', kit.subtitle);
      setValue('shortDescription', kit.shortDescription);
    }
  }, []);

  const kitResourceIdForKitResource = (kitResource: KitResource): KitResourceId => {
    switch (kitResource.type) {
      case DiscoverItemType.KIT:
        return { id: kitResource.kit.id, type: DiscoverItemType.KIT };
      case DiscoverItemType.TEMPLATE:
        return { id: kitResource.template.id, type: DiscoverItemType.TEMPLATE };
    }
  };

  return (
    <form
      id={formId}
      onSubmit={handleSubmit((data: Partial<Kits>) => {
        const formData = {
          ...data,
          longDescription: updatedKit.longDescription,
          instructions: updatedKit.instructions,
          bannerImageUrl: updatedKit.bannerImageUrl,
          thumbnailImageUrl: updatedKit.thumbnailImageUrl,
          tags: updatedKit.tags,
          metadata: updatedKit.metadata,
          branding: updatedKit.branding,
          kitResourceIds: updatedKit.kitResourceIds,
          visibleInDiscover: updatedKit.visibleInDiscover,
          orgTypeFiltering: updatedKit.orgTypeFiltering,
        };
        return handleKitSave(formData);
      })}
    >
      <Input
        label="Title"
        className="mb-4"
        value={updatedKit.title}
        error={errors.title?.message.toString()}
        onChangeText={(t) => {
          setUpdatedKit((prevState) => {
            return { ...prevState, title: t };
          });
          handleOnChange((prevState) => {
            return { ...prevState, title: t };
          });
        }}
        required
        {...register('title')}
      />
      <Input
        label="Subtitle"
        className="mb-4"
        value={updatedKit.subtitle}
        onChangeText={(t) => {
          setUpdatedKit((prevState) => {
            return { ...prevState, subtitle: t };
          });
          handleOnChange((prevState) => {
            return { ...prevState, subtitle: t };
          });
        }}
        {...register('subtitle')}
      />
      <div className="mb-4">
        <Typography className="text-sm text-glooCharcoal font-semibold pb-2" variant="base">
          Display In Discover
        </Typography>
        <ToggleControlled
          isChecked={updatedKit?.visibleInDiscover}
          onChange={(value) => {
            setUpdatedKit((prevState) => ({
              ...prevState,
              visibleInDiscover: value,
            }));
            handleOnChange((prevState) => ({
              ...prevState,
              visibleInDiscover: value,
            }));
          }}
        />
      </div>

      <div className="mb-4">
        <Typography className="text-sm text-glooCharcoal font-semibold pb-2" variant="base">
          Org Type Filtering
        </Typography>
        <Select
          dataDurableId="orgTypeFilteringSelect"
          options={orgTypeFilteringOptions}
          onChange={(option: SelectOption) => {
            setUpdatedKit((prevState) => ({
              ...prevState,
              orgTypeFiltering: option.value,
            }));
            handleOnChange((prevState) => ({
              ...prevState,
              orgTypeFiltering: option.value,
            }));
          }}
          value={orgTypeFilteringOptions.find((o) => o.value === updatedKit?.orgTypeFiltering)}
        />
      </div>

      <SelectKeywords
        dataDurableId="kitFormSelectId"
        className="mb-4 border"
        label={
          <Typography className="text-sm text-glooCharcoal font-semibold pl-1" variant="base">
            Topics
          </Typography>
        }
        placeholder="Choose Topics"
        noOptionsMessage={() => 'Type a Topic and press Enter'}
        startingKeywords={updatedKit.metadata?.topics || []}
        onChange={(t) => {
          handleOnChange((prevState) => {
            return {
              ...prevState,
              tags: [...t],
              metadata: {
                ...prevState.metadata,
                topics: [...t],
              },
            };
          });

          setUpdatedKit((prevState) => {
            return {
              ...prevState,
              tags: [...t],
              metadata: {
                ...prevState.metadata,
                topics: [...t],
              },
            };
          });
        }}
        onCreate={(t) => {
          handleOnChange((prevState) => {
            return {
              ...prevState,
              tags: [...(prevState?.tags || []), t],
              metadata: {
                ...prevState.metadata,
                topics: prevState?.metadata?.topics ? [...prevState.metadata.topics, t] : [t],
              },
            };
          });

          setUpdatedKit((prevState) => {
            return {
              ...prevState,
              tags: [...(prevState?.tags || []), t],
              metadata: {
                ...prevState.metadata,
                topics: prevState?.metadata?.topics ? [...prevState.metadata.topics, t] : [t],
              },
            };
          });
        }}
      />

      <Typography className="text-sm text-glooCharcoal font-semibold pl-1 pb-2" variant="base">
        Banner Image
      </Typography>

      <CloudinaryImageUploader
        imageWidth={600}
        preview
        className="mb-4"
        fileFilter="image/*,video/*"
        existingImg={updatedKit.bannerImageUrl}
        placeholder={KIT_BANNER}
        handleImgSelect={(imgUrl) => {
          setUpdatedKit((prevState) => {
            return {
              ...prevState,
              bannerImageUrl: imgUrl,
            };
          });
          handleOnChange((prevState) => {
            return {
              ...prevState,
              bannerImageUrl: imgUrl,
            };
          });
        }}
      />

      <Typography className="text-sm text-glooCharcoal font-semibold pl-1 pb-2" variant="base">
        Thumbnail Image
      </Typography>

      <CloudinaryImageUploader
        imageWidth={213}
        preview
        className="mb-4"
        existingImg={updatedKit.thumbnailImageUrl}
        placeholder={KIT_THUMBNAIL}
        handleImgSelect={(imgUrl) => {
          setUpdatedKit((prevState) => {
            return {
              ...prevState,
              thumbnailImageUrl: imgUrl,
            };
          });
          handleOnChange((prevState) => {
            return {
              ...prevState,
              thumbnailImageUrl: imgUrl,
            };
          });
        }}
      />

      <Input
        label="Short Description"
        isMultiLine
        rows={4}
        className="mb-4"
        value={updatedKit.shortDescription}
        error={errors.shortDescription?.message.toString()}
        onChangeText={(t) => {
          setUpdatedKit((prevState) => {
            return { ...prevState, shortDescription: t };
          });
          handleOnChange((prevState) => {
            return { ...prevState, shortDescription: t };
          });
        }}
        required
        {...register('shortDescription')}
      />

      <Input
        label="Author"
        className="mb-4"
        value={updatedKit.metadata?.author}
        onChangeText={(t) => {
          setUpdatedKit((prevState) => {
            return { ...prevState, metadata: { ...prevState.metadata, author: t } };
          });
          handleOnChange((prevState) => {
            return { ...prevState, metadata: { ...prevState.metadata, author: t } };
          });
        }}
        {...register('metadata.author')}
      />

      <Typography className="text-sm text-glooCharcoal font-semibold pl-1 pb-2" variant="base">
        Branding
      </Typography>
      <Card className="flex">
        <div className="flex-none">
          <Typography className="text-sm text-glooCharcoal font-semibold pl-1 pb-2" variant="label">
            Settings
          </Typography>
          <Separator className="mb-4" />
          <div className="mb-4">
            <Typography className="text-sm text-glooCharcoal font-semibold pl-1 pb-2" variant="base">
              Title Color
            </Typography>
            <CompactPicker
              color={updatedKit.branding?.titleColor || '#000'}
              onChangeComplete={(color) => {
                setUpdatedKit((prevState) => {
                  return {
                    ...prevState,
                    branding: {
                      ...prevState.branding,
                      titleColor: color.hex as HexColor<string>,
                    },
                  };
                });
                handleOnChange((prevState) => {
                  return {
                    ...prevState,
                    branding: {
                      ...prevState.branding,
                      titleColor: color.hex as HexColor<string>,
                    },
                  };
                });
              }}
            />
          </div>

          <div className="mb-4">
            <Typography className="text-sm text-glooCharcoal font-semibold pl-1 pb-2" variant="base">
              Text Color
            </Typography>
            <CompactPicker
              color={updatedKit.branding?.textColor || '#000'}
              onChangeComplete={(color) => {
                setUpdatedKit((prevState) => {
                  return {
                    ...prevState,
                    branding: {
                      ...prevState.branding,
                      textColor: color.hex as HexColor<string>,
                    },
                  };
                });
                handleOnChange((prevState) => {
                  return {
                    ...prevState,
                    branding: {
                      ...prevState.branding,
                      textColor: color.hex as HexColor<string>,
                    },
                  };
                });
              }}
            />
          </div>

          <div className="mb-4">
            <Typography className="text-sm text-glooCharcoal font-semibold pl-1 pb-2" variant="base">
              Background Color
            </Typography>
            <CompactPicker
              color={updatedKit.branding?.backgroundColor || '#ccc'}
              onChangeComplete={(color) => {
                setUpdatedKit((prevState) => {
                  return {
                    ...prevState,
                    branding: {
                      ...prevState.branding,
                      backgroundColor: color.hex as HexColor<string>,
                    },
                  };
                });
                handleOnChange((prevState) => {
                  return {
                    ...prevState,
                    branding: {
                      ...prevState.branding,
                      backgroundColor: color.hex as HexColor<string>,
                    },
                  };
                });
              }}
            />
          </div>

          <Typography className="text-sm text-glooCharcoal font-semibold pl-1 pb-2" variant="base">
            Brand Logo
          </Typography>

          <CloudinaryImageUploader
            imageWidth={213}
            preview
            className="mb-4"
            existingImg={updatedKit.branding?.logoImageUrl}
            handleImgSelect={(imgUrl) => {
              setUpdatedKit((prevState) => {
                return {
                  ...prevState,
                  branding: {
                    ...prevState.branding,
                    logoImageUrl: imgUrl,
                  },
                };
              });
              handleOnChange((prevState) => {
                return {
                  ...prevState,
                  branding: {
                    ...prevState.branding,
                    logoImageUrl: imgUrl,
                  },
                };
              });
            }}
          />
        </div>
        <div className="flex-auto ml-10 mr-6">
          <Typography className="text-sm text-glooCharcoal font-semibold pl-1 pb-2 mb-6" variant="h3">
            Preview
          </Typography>
          <KitBannerPreview
            kit={
              {
                id: 'authoring-preview',
                ...updatedKit,
              } as Kits
            }
          />
        </div>
      </Card>

      <Typography className="pl-1 pb-2" variant="label">
        Long Description
        <span className="text-red text-xs">*</span>
      </Typography>
      <WysiwygEditor
        className="mb-4"
        resources={updatedKit.kitResources}
        minDivHeight="616px"
        height={600}
        value={updatedKit.longDescription}
        onChange={(value) => {
          setUpdatedKit((prevState) => {
            return { ...prevState, longDescription: value };
          });
          handleOnChange((prevState) => {
            return { ...prevState, longDescription: value };
          });
        }}
      />
      <Typography className="pl-1 pb-2" variant="label">
        Instructions
        <span className="text-red text-xs">*</span>
      </Typography>
      <WysiwygEditor
        className="mb-2"
        minDivHeight="616px"
        height={600}
        value={updatedKit.instructions}
        resources={updatedKit.kitResources}
        onChange={(value) => {
          setUpdatedKit((prevState) => {
            return { ...prevState, instructions: value };
          });
          handleOnChange((prevState) => {
            return { ...prevState, instructions: value };
          });
        }}
      />

      <Card className="mb-10">
        <Typography className="text-thryveGray my-4" variant="h3">
          RESOURCES:
        </Typography>
        <KitAuthoringResourcesList
          kitId={updatedKit.id}
          kitResources={updatedKit.kitResources}
          handleKitResourcesUpdated={(newKitResources) => {
            setUpdatedKit((prevState) => {
              return {
                ...prevState,
                kitResources: newKitResources,
                kitResourceIds: newKitResources.map(kitResourceIdForKitResource),
              };
            });
            handleOnChange((prevState) => {
              return {
                ...prevState,
                kitResources: newKitResources,
                kitResourceIds: newKitResources.map(kitResourceIdForKitResource),
              };
            });
          }}
        />
      </Card>
    </form>
  );
}

export default KitForm;
